import {
  ActionIcon,
  Box,
  Menu,
  Popover,
  Tooltip,
  TextInput,
  Button as MantineButton,
} from '@mantine/core'
import { useState, type ReactNode } from 'react'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import {
  UPDATE_TRACKING_LINK,
  GET_TRACKING_LINKS,
} from 'src/graphql/trackinglinks.graphql'
import IconClose from 'src/icons/IconClose'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconCopy from 'src/icons/IconCopy'
import IconLink from 'src/icons/IconLink'
import IconAdd from 'src/icons/IconAdd'
import EmployeeAvatarGroupForJobListing from 'src/components/Core/EmployeeAvatarGroupForJobListing'
import { buildTrackingLinkUrl } from 'src/lib/url.utils'
import SettingsField from 'src/pages/SettingsPage/components/SettingsField'

type AssignmentOption = {
  employeeId: string
  label: string
  status?: string | null
}

type TrackingLinkProps = {
  link: any
  jobListingId: string
  handleDeleteModal: (linkId: string) => void
  showDeleteButton?: boolean
  isEditMode?: boolean
  onUpdateLinkName?: (trackingLinkId: string, linkName: string) => void
  allEmployeeOptions?: AssignmentOption[]
  /** Rendered directly after the link name, e.g. Indeed's expand control. */
  titleSuffix?: ReactNode
}

const TrackingLink = ({
  link,
  jobListingId,
  handleDeleteModal,
  showDeleteButton = true,
  isEditMode = false,
  onUpdateLinkName,
  allEmployeeOptions = [],
  titleSuffix = null,
}: TrackingLinkProps) => {
  const [isEditingName, setIsEditingName] = useState(false)
  const [linkName, setLinkName] = useState(link.linkName?.trim() || '')
  const [isAssignmentsOpen, setIsAssignmentsOpen] = useState(false)

  // Get assigned employee IDs directly from the link data
  const assignedLeadEmployeeIds =
    link.leadEmployees?.map((e) => e.employeeId) ?? []
  const assignedMemberEmployeeIds =
    link.memberEmployees?.map((e) => e.employeeId) ?? []

  const [updateTrackingLink] = useMutation(UPDATE_TRACKING_LINK, {
    refetchQueries: [
      { query: GET_TRACKING_LINKS, variables: { jobListingId } },
    ],
    onError: () => {
      toast('Unable to update link, please try again.', 'error')
    },
  })

  const generateLink = () => {
    return buildTrackingLinkUrl(link.link)
  }

  const uniqueLink = generateLink()
  const linkTypeDisplay = LinkTypeDisplay[link.linkType] || link.linkType

  const handleSaveLinkName = () => {
    const newLinkName = linkName.trim()
    const originalLinkName = link.linkName?.trim() || ''

    // Only make API call if the name actually changed
    if (newLinkName !== originalLinkName && onUpdateLinkName) {
      onUpdateLinkName(link.trackingLinkId, newLinkName)
    }
    setIsEditingName(false)
  }

  const handleCancelEdit = () => {
    setLinkName(link.linkName?.trim() || '')
    setIsEditingName(false)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveLinkName()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  const employeeOptionsById = new Map(
    allEmployeeOptions.map((option) => [option.employeeId, option])
  )

  // Get assigned employees with their details
  const assignedLeadEmployees = assignedLeadEmployeeIds
    .map((id) => employeeOptionsById.get(id))
    .filter(Boolean) as AssignmentOption[]
  const assignedMemberEmployees = assignedMemberEmployeeIds
    .map((id) => employeeOptionsById.get(id))
    .filter(Boolean) as AssignmentOption[]
  const assignedEmployees = [...assignedLeadEmployees, ...assignedMemberEmployees]

  // All assigned employee IDs (for checking availability)
  const allAssignedIds = new Set([
    ...assignedLeadEmployeeIds,
    ...assignedMemberEmployeeIds,
  ])

  // Available employees for lead assignment (not already assigned as lead or member)
  const availableLeadAssignmentOptions = allEmployeeOptions.filter(
    (option) => !allAssignedIds.has(option.employeeId)
  )
  // Available employees for member assignment (not already assigned as lead or member)
  const availableMemberAssignmentOptions = allEmployeeOptions.filter(
    (option) => !allAssignedIds.has(option.employeeId)
  )

  const updateEmployeeAssignments = (
    nextLeadIds: string[],
    nextMemberIds: string[]
  ) => {
    updateTrackingLink({
      variables: {
        id: link.trackingLinkId,
        input: {
          leadEmployeeIds: nextLeadIds,
          memberEmployeeIds: nextMemberIds,
        },
      },
      optimisticResponse: {
        updateTrackingLink: {
          __typename: 'TrackingLink',
          trackingLinkId: link.trackingLinkId,
          linkName: link.linkName,
          leadEmployees: nextLeadIds.map((id) => ({
            __typename: 'Employee',
            employeeId: id,
          })),
          memberEmployees: nextMemberIds.map((id) => ({
            __typename: 'Employee',
            employeeId: id,
          })),
        },
      },
    })
  }

  const handleAddLead = (employeeId: string) => {
    updateEmployeeAssignments(
      [...assignedLeadEmployeeIds, employeeId],
      assignedMemberEmployeeIds
    )
  }

  const handleRemoveLead = (employeeId: string) => {
    updateEmployeeAssignments(
      assignedLeadEmployeeIds.filter((id) => id !== employeeId),
      assignedMemberEmployeeIds
    )
  }

  const handleAddMember = (employeeId: string) => {
    updateEmployeeAssignments(
      assignedLeadEmployeeIds,
      [...assignedMemberEmployeeIds, employeeId]
    )
  }

  const handleRemoveMember = (employeeId: string) => {
    updateEmployeeAssignments(
      assignedLeadEmployeeIds,
      assignedMemberEmployeeIds.filter((id) => id !== employeeId)
    )
  }

  const assignmentsDisplay = (
    <div
      className={`flex items-center gap-1 ${isEditMode ? 'cursor-pointer' : ''}`}
    >
      {assignedEmployees.length === 0 ? (
        <Tooltip label="No lead or member employees assigned to this link">
          <span className="text-xs text-doubleNickel-gray-500">-</span>
        </Tooltip>
      ) : (
        <EmployeeAvatarGroupForJobListing
          leadEmployees={assignedLeadEmployees}
          memberEmployees={assignedMemberEmployees}
        />
      )}
      {isEditMode && (
        <IconChevronDown className="h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-700" />
      )}
    </div>
  )

  const linkNameDisplay = isEditMode && isEditingName ? (
    <TextInput
      size="xs"
      value={linkName}
      onChange={(e) => setLinkName(e.target.value)}
      onKeyDown={handleKeyPress}
      onBlur={handleSaveLinkName}
      placeholder="Link name"
      className="ml-2"
      style={{ width: '180px', fontSize: '12px' }}
      autoFocus
    />
  ) : (
    <>
      {link.linkName?.trim() ? (
        <span
          className={`text-xs font-normal text-doubleNickel-gray-900 ${isEditMode ? 'cursor-pointer hover:bg-gray-100 px-1 rounded' : ''}`}
          onClick={() => isEditMode && setIsEditingName(true)}
          data-row-toggle-ignore={isEditMode || undefined}
        >
          {link.linkName.trim()}
        </span>
      ) : isEditMode ? (
        <span
          className="text-xs font-normal text-doubleNickel-gray-500 cursor-pointer hover:bg-gray-100 px-1 rounded"
          onClick={() => setIsEditingName(true)}
          data-row-toggle-ignore
        >
          -
        </span>
      ) : null}
    </>
  )

  return (
    <SettingsField
      key={link.trackingLinkId}
      title={<span className="text-xs font-normal">{linkTypeDisplay}</span>}
      titleRight={
        <span className="ml-1 flex items-center gap-1">
          {linkNameDisplay}
          {titleSuffix}
        </span>
      }
    >
      <div className="flex flex-row items-center gap-2 min-h-[32px]">
        {isEditMode ? (
          <Popover
            opened={isAssignmentsOpen}
            onChange={setIsAssignmentsOpen}
            position="bottom-end"
            shadow="md"
            width={240}
          >
            <Popover.Target>
              <Box
                className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-doubleNickel-gray-50"
                onClick={() => setIsAssignmentsOpen(!isAssignmentsOpen)}
                data-row-toggle-ignore
              >
                {assignmentsDisplay}
              </Box>
            </Popover.Target>
            <Popover.Dropdown className="p-2">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full flex-col gap-1">
                  <div className="text-[11px] font-medium text-doubleNickel-gray-500">
                    Lead Employees ({assignedLeadEmployees.length})
                  </div>
                  <div className="flex w-full flex-col gap-1.5">
                    {assignedLeadEmployees.length === 0 && (
                      <div className="rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5 text-xs text-doubleNickel-gray-500">
                        No lead employees
                      </div>
                    )}
                    {assignedLeadEmployees.map((employee) => (
                      <div
                        key={employee.employeeId}
                        className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
                      >
                        <span className="text-xs text-doubleNickel-gray-900">
                          {employee.label}
                        </span>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size={20}
                          onClick={() => handleRemoveLead(employee.employeeId)}
                        >
                          <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                        </ActionIcon>
                      </div>
                    ))}
                    <Menu
                      shadow="md"
                      position="bottom-start"
                      width="target"
                      withinPortal={false}
                    >
                      <Menu.Target>
                        <MantineButton
                          variant="subtle"
                          color="gray"
                          size="xs"
                          className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                          leftSection={
                            <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                          }
                        >
                          Add Lead
                        </MantineButton>
                      </Menu.Target>
                      <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                        {availableLeadAssignmentOptions.length === 0 ? (
                          <Menu.Item disabled>No employees available</Menu.Item>
                        ) : (
                          availableLeadAssignmentOptions.map((employee) => (
                            <Menu.Item
                              key={employee.employeeId}
                              onClick={() => handleAddLead(employee.employeeId)}
                            >
                              {employee.label}
                            </Menu.Item>
                          ))
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-1">
                  <div className="text-[11px] font-medium text-doubleNickel-gray-500">
                    Members ({assignedMemberEmployees.length})
                  </div>
                  <div className="flex w-full flex-col gap-1.5">
                    {assignedMemberEmployees.length === 0 && (
                      <div className="rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5 text-xs text-doubleNickel-gray-500">
                        No member employees
                      </div>
                    )}
                    {assignedMemberEmployees.map((employee) => (
                      <div
                        key={employee.employeeId}
                        className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
                      >
                        <span className="text-xs text-doubleNickel-gray-900">
                          {employee.label}
                        </span>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size={20}
                          onClick={() => handleRemoveMember(employee.employeeId)}
                        >
                          <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                        </ActionIcon>
                      </div>
                    ))}
                    <Menu
                      shadow="md"
                      position="bottom-start"
                      width="target"
                      withinPortal={false}
                    >
                      <Menu.Target>
                        <MantineButton
                          variant="subtle"
                          color="gray"
                          size="xs"
                          className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                          leftSection={
                            <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                          }
                        >
                          Add Member
                        </MantineButton>
                      </Menu.Target>
                      <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                        {availableMemberAssignmentOptions.length === 0 ? (
                          <Menu.Item disabled>No employees available</Menu.Item>
                        ) : (
                          availableMemberAssignmentOptions.map((employee) => (
                            <Menu.Item
                              key={employee.employeeId}
                              onClick={() => handleAddMember(employee.employeeId)}
                            >
                              {employee.label}
                            </Menu.Item>
                          ))
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                </div>
              </div>
            </Popover.Dropdown>
          </Popover>
        ) : (
          assignmentsDisplay
        )}
        <Tooltip label="Copy tracking link ID">
          <ActionIcon
            size={16}
            variant="subtle"
            color="gray"
            onClick={() => {
              navigator.clipboard.writeText(link.trackingLinkId)
              toast('Tracking link ID copied to clipboard', 'success')
            }}
          >
            <IconCopy className="h-3 fill-none stroke-doubleNickel-gray-700" />
          </ActionIcon>
        </Tooltip>

        <Tooltip label="Copy tracking link URL">
          <ActionIcon
            size={16}
            variant="subtle"
            color="gray"
            onClick={() => {
              navigator.clipboard.writeText(uniqueLink)
              toast('Tracking link copied to clipboard', 'success')
            }}
          >
            <IconLink className="h-3 fill-none stroke-doubleNickel-gray-700" />
          </ActionIcon>
        </Tooltip>

        {showDeleteButton && (
          <Tooltip label="Delete tracking link">
            <ActionIcon
              size={16}
              variant="subtle"
              color="gray"
              onClick={() => handleDeleteModal(link.trackingLinkId)}
            >
              <IconClose className="h-3 fill-none stroke-doubleNickel-gray-700" />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
    </SettingsField>
  )
}

export default TrackingLink
